import { APP_NAME, CURRENT_ENV } from "./constants/environment.const";

import { ApplicationInsights } from "@microsoft/applicationinsights-web";
// AppInsights.js
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import packageJson from "../package.json";

// TypeScript interfaces for Application Insights context
interface TelemetryTrace {
  traceID?: string;
}

interface Operation {
  id?: string;
}

interface AppInsightsContext {
  telemetryTrace?: TelemetryTrace;
  operation?: Operation;
}

const reactPlugin = new ReactPlugin();
const appInsights = new ApplicationInsights({
  config: {
    connectionString: import.meta.env.VITE_APPINSIGHT_CONNECTION_STRING,
    enableCorsCorrelation: true,
    correlationHeaderDomains: [import.meta.env.BASE_URL],
    enableAutoRouteTracking: true,
    extensions: [reactPlugin],
    disableTelemetry: CURRENT_ENV === "local",
  },
});
appInsights.loadAppInsights();

// Add global properties to identify this specific app
appInsights.addTelemetryInitializer((envelope) => {
  if (envelope.tags) {
    envelope.tags["ai.cloud.role"] = APP_NAME;
    envelope.tags["ai.cloud.roleInstance"] = `parking-drawer-${window.location.hostname}`;
  }

  // Add custom properties to all telemetry
  if (envelope.data && envelope.data.baseData) {
    envelope.data.baseData.properties = {
      ...envelope.data.baseData.properties,
      appName: "parking-drawer",
      appType: "react-frontend",
      appVersion: packageJson.version,
      environment: CURRENT_ENV,
    };
  }

  return true;
});

export { appInsights, reactPlugin };

// Get W3C correlation headers for proper Application Insights correlation
export const getCorrelationHeaders = (): Record<string, string> => {
  try {
    const context = appInsights.context;
    const telemetryTrace = (context as AppInsightsContext)?.telemetryTrace;
    const operationId = (context as AppInsightsContext)?.operation?.id;

    const correlationId = telemetryTrace?.traceID || operationId || crypto.randomUUID();

    return {
      "X-Correlation-ID": correlationId,
      "X-App-Name": APP_NAME,
    };
  } catch {
    const fallbackId = crypto.randomUUID();
    return {
      "X-Correlation-ID": fallbackId,
      "X-App-Name": APP_NAME,
    };
  }
};

export const trackUserAction = (actionName: string, properties?: Record<string, unknown>) => {
  appInsights.trackEvent({
    name: `UserAction_${actionName}`,
    properties: {
      actionType: "user-interaction",
      ...properties,
    },
  });
};

export const trackApiCall = (operationName: string, properties?: Record<string, unknown>) => {
  appInsights.trackEvent({
    name: "API_Call_Started",
    properties: {
      operationName,
      actionType: "api-call",
      ...properties,
    },
  });
};

export const trackApiResponse = (
  operationName: string,
  success: boolean,
  duration?: number,
  properties?: Record<string, unknown>,
) => {
  appInsights.trackEvent({
    name: "API_Call_Completed",
    properties: {
      operationName,
      success,
      duration,
      actionType: "api-response",
      ...properties,
    },
  });
};
